import { EncodeObject } from '@cosmjs/proto-signing'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Any } from 'cosmjs-types/google/protobuf/any'
import { generatePath, useNavigate } from 'react-router-dom'

import { fetchMultisigProposals } from '@/api'
import { useKeplrWallet } from '@/contexts/KeplrWalletProvider'
import { emitter, ErrorHandler, formatAddress } from '@/helpers'
import { buildSubmitMultisigProposalMsg } from '@/libs/keplr/messages'
import { MultisigError } from '@/libs/keplr/messages/errors'
import { queryKeys } from '@/queries/keys'
import { RoutePaths } from '@/routes'
import { ProtobufAnyMessage } from '@/types'

import { moduleRegistry } from '../moduleRegistry'
import { parseProposalJsonMessages } from '../utils/parse-proposal-json'

function encodeJsonMessagesToAny(messages: ProtobufAnyMessage[]): Any[] {
  return messages.map(message => {
    const typeUrl = String(message['@type'] ?? '')
    const { ['@type']: _, ...messageValue } = message

    const encodeObject: EncodeObject = {
      typeUrl,
      value: messageValue,
    }

    const value = moduleRegistry.encode(encodeObject)

    return Any.fromPartial({ typeUrl, value })
  })
}

export function useSubmitMultisigProposal({ group }: { group: string }) {
  const { address, signAndBroadcast } = useKeplrWallet()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ rawJsonText }: { rawJsonText: string }) => {
      if (!address) {
        throw new Error('Wallet not connected')
      }

      const messages = parseProposalJsonMessages(rawJsonText)
      const anyMessages = encodeJsonMessagesToAny(messages)

      const submitProposalMsg = buildSubmitMultisigProposalMsg({
        creator: address,
        group,
        messages: anyMessages,
      })

      const txHash = await signAndBroadcast([submitProposalMsg])

      return { txHash }
    },
    onSuccess: async result => {
      emitter.emit('success', {
        title: 'Proposal submitted',
        message: `TxHash: ${formatAddress(result.txHash)}`,
      })

      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: queryKeys.multisig.groupByAccount(group),
        }),
        queryClient.invalidateQueries({
          queryKey: queryKeys.multisig.proposalsListRoot(),
        }),
        queryClient.invalidateQueries({
          queryKey: queryKeys.multisig.groupsListRoot(),
        }),
      ])

      // Fetch latest proposal to get its ID and redirect to it
      try {
        const proposalsResponse = await fetchMultisigProposals({
          limit: '1',
          reverse: true,
        })
        const latestProposal = proposalsResponse.proposal[0]

        if (latestProposal) {
          navigate(
            generatePath(RoutePaths.MultisigGroupProposal, {
              account: group,
              proposalId: latestProposal.id,
            }),
          )
          return
        }

        navigate(generatePath(RoutePaths.MultisigGroup, { account: group }))
      } catch {
        navigate(generatePath(RoutePaths.MultisigGroup, { account: group }))
      }
    },
    onError: error => {
      ErrorHandler.process(MultisigError.fromUnknown(error))
    },
  })
}
